import { useCallback, useState } from 'react';
import type { GestureResponderEvent } from 'react-native';
import { Pressable, View } from 'react-native';

import { Box, HStack, Text, VStack, theme } from '@squire/ui';

import { InfoCircleIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { formatPrice } from '@/utils/internationalizationUtils';
import { isAddonService } from '@/utils/isAddonService';
import { resolveLocalizedText } from '@/utils/localizedText';

import { PriceRangeTooltip } from '../PriceRangeTooltip';
import { styles } from './styles';
import type { ServiceCardV2Props } from './types';

function ServiceCardV2({
  service,
  selected,
  onPress,
  testID,
  style,
  currency = 'USD',
  showPriceRangeTooltip = false,
  categoryName,
  ...props
}: ServiceCardV2Props) {
  const { t, i18n } = useScreenTranslations('ServiceCard');

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const isSelected = !!selected;
  const isPrimary = selected === 'primary';

  const variantState =
    selected === 'primary'
      ? 'selected'
      : selected === 'addon'
        ? 'addon'
        : undefined;
  styles.useVariants({ state: variantState });

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} ${t('duration.minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}${t('duration.hours')}`;
    }
    return `${hours}${t('duration.hours')} ${remainingMinutes}${t('duration.minutesShort')}`;
  };

  const handleCardPress = useCallback(() => {
    onPress?.();
  }, [onPress]);

  const handleToggleDescription = useCallback(
    (event?: GestureResponderEvent) => {
      // Stop propagation on web so the parent card Pressable doesn't also fire
      event?.stopPropagation?.();
      setDescriptionExpanded((prev) => !prev);
    },
    [],
  );

  const description =
    resolveLocalizedText(service.localizedDesc, i18n.language) || service.desc;

  const { costRange, durationRange } = service;

  const hasPriceRange =
    costRange !== undefined && costRange.min !== costRange.max;

  const formattedPrice = hasPriceRange
    ? `${formatPrice(costRange.min / 100, { locale: i18n.language, currency })} - ${formatPrice(costRange.max / 100, { locale: i18n.language, currency })}`
    : formatPrice(service.cost / 100, { locale: i18n.language, currency });

  const hasDurationRange =
    durationRange !== undefined && durationRange.min !== durationRange.max;

  const durationText = hasDurationRange
    ? `${formatDuration(durationRange.min)} - ${formatDuration(durationRange.max)}`
    : formatDuration(service.duration);

  const isAddon = isAddonService(service);

  if (isSelected) {
    return (
      <Pressable
        {...props}
        onPress={handleCardPress}
        style={[styles.container]}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={t('accessibility.serviceDetails', {
          name: service.name,
          duration: durationText,
          price: formattedPrice,
        })}
        accessibilityHint={t('accessibility.currentlySelected')}
        {...pressedState(true)}
      >
        {({ pressed }) => (
          <Box style={[styles.content({ pressed }), style]}>
            <VStack space={theme.spacing[4]}>
              <HStack space={theme.spacing[3]} style={styles.header}>
                <VStack space={theme.spacing[2]} style={styles.headerInfo}>
                  <Text
                    variant="mStrong"
                    color={isPrimary ? 'textPrimaryInverse' : 'textPrimary'}
                    numberOfLines={4}
                  >
                    {service.name}
                  </Text>
                  {!isPrimary && isAddon ? (
                    <View style={styles.addonBadge}>
                      <Text variant="sStrong" color="textBrand">
                        {t('badge.addon')}
                      </Text>
                    </View>
                  ) : (
                    <Text
                      variant="s"
                      color={isPrimary ? 'textTertiary' : 'textSecondary'}
                      style={styles.duration}
                    >
                      {durationText}
                    </Text>
                  )}
                  {!!categoryName && (
                    <Text
                      variant="s"
                      color={isPrimary ? 'textTertiary' : 'textSecondary'}
                    >
                      {categoryName}
                    </Text>
                  )}
                </VStack>
                <View
                  pointerEvents="none"
                  style={
                    isPrimary
                      ? styles.removeButtonPrimary
                      : styles.removeButtonAddon
                  }
                >
                  <View
                    style={
                      isPrimary
                        ? styles.minusLinePrimary
                        : styles.minusLineAddon
                    }
                  />
                </View>
              </HStack>
              <View style={styles.priceSpacer} />
            </VStack>
            <View
              style={
                isPrimary ? styles.priceBadgePrimary : styles.priceBadgeAddon
              }
            >
              <HStack centerVertical space={theme.spacing[2]}>
                <Text
                  variant="sStrong"
                  color={isPrimary ? 'textPrimaryInverse' : 'textPrimary'}
                >
                  {formattedPrice}
                </Text>
                {showPriceRangeTooltip && hasPriceRange && (
                  <PriceRangeTooltip testID={`${testID}-price-range-tooltip`} />
                )}
              </HStack>
            </View>
          </Box>
        )}
      </Pressable>
    );
  }

  return (
    <Pressable
      {...props}
      onPress={handleCardPress}
      style={[styles.container]}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={t('accessibility.serviceDetails', {
        name: service.name,
        duration: durationText,
        price: formattedPrice,
      })}
      accessibilityHint={t('accessibility.tapToSelect')}
      {...pressedState(false)}
    >
      {({ pressed }) => (
        <Box style={[styles.content({ pressed }), style]}>
          <VStack space={theme.spacing[4]}>
            <HStack space={theme.spacing[3]} style={styles.header}>
              <VStack space={theme.spacing[2]} style={styles.headerInfo}>
                <Text variant="mStrong" color="textPrimary" numberOfLines={4}>
                  {service.name}
                </Text>
                {isAddon ? (
                  <View style={styles.addonBadge}>
                    <Text variant="sStrong" color="textBrand">
                      {t('badge.addon')}
                    </Text>
                  </View>
                ) : (
                  <Text
                    variant="s"
                    color="textSecondary"
                    style={styles.duration}
                  >
                    {durationText}
                  </Text>
                )}
              </VStack>
              {!!description && (
                <Pressable
                  onPress={handleToggleDescription}
                  hitSlop={theme.spacing[3]}
                  testID={testID && `${testID}-info-button`}
                  accessibilityRole="button"
                  accessibilityLabel={
                    descriptionExpanded
                      ? t('description.lessDetails')
                      : t('description.moreDetails')
                  }
                  aria-expanded={descriptionExpanded}
                >
                  <InfoCircleIcon
                    size={24}
                    strokeWidth={1.5}
                    color={descriptionExpanded ? 'iconPrimary' : 'iconTertiary'}
                  />
                </Pressable>
              )}
            </HStack>
            {descriptionExpanded && !!description && (
              <Text variant="s" color="textSecondary">
                {description}
              </Text>
            )}

            <View style={styles.priceSpacer} />
          </VStack>
          <Box style={styles.price}>
            <HStack centerVertical space={theme.spacing[2]}>
              <Text variant="sStrong" color="textPrimary">
                {formattedPrice}
              </Text>
              {showPriceRangeTooltip && hasPriceRange && (
                <PriceRangeTooltip testID={`${testID}-price-range-tooltip`} />
              )}
            </HStack>
          </Box>
        </Box>
      )}
    </Pressable>
  );
}

ServiceCardV2.displayName = 'ServiceCardV2';
export { ServiceCardV2 };
